import {
  IntegrationStatusFilters,
  sortFilter,
} from '../../../constants/integrationsList.js';
import { Platform } from '../../../types/common.js';
import { FilterType } from '../../features/Filters/Filters.types.js';

export const isIntegrationBelongsToCategory = (
  integration: Platform,
  selectedFilters: string[],
): boolean =>
  selectedFilters.some((filterItem) => integration.category === filterItem);

export const isIntegrationInChannel = (
  integration: Platform,
  selectedFilters: string[],
): boolean =>
  integration?.channels?.some((el) => selectedFilters.find((id) => el === id));

function sortByLabel(a: Platform, b: Platform) {
  if (a[sortFilter.label] < b[sortFilter.label]) {
    return -1;
  }
  if (a[sortFilter.label] > b[sortFilter.label]) {
    return 1;
  }
  return 0;
}

function filterByStatus(
  integration: Platform,
  statusFilter: IntegrationStatusFilters,
): boolean {
  if (statusFilter !== IntegrationStatusFilters.all) {
    if (statusFilter === IntegrationStatusFilters.notIntegrated) {
      return !integration.isIntegrated;
    } else {
      return !!integration.isIntegrated;
    }
  }

  return !!integration;
}

function filterByActiveFilters(
  integration: Platform,
  categories: string[],
  channels: string[],
): boolean {
  const isAnyChannelSelected = channels.length > 0;
  const isAnyCategorySelected = categories.length > 0;

  if (isAnyChannelSelected || isAnyCategorySelected) {
    if (isAnyChannelSelected && isAnyCategorySelected) {
      return (
        isIntegrationInChannel(integration, channels) &&
        isIntegrationBelongsToCategory(integration, categories)
      );
    }
    if (isAnyChannelSelected) {
      return isIntegrationInChannel(integration, channels);
    }
    if (isAnyCategorySelected) {
      return isIntegrationBelongsToCategory(integration, categories);
    }

    return false;
  }

  return true;
}

function filterBySearchValue(
  integration: Platform,
  searchValue?: string,
): boolean {
  if (searchValue) {
    return integration.label.toLowerCase().includes(searchValue.toLowerCase());
  }

  return true;
}

function sortIntegrations(
  integrations: Platform[],
  sortField: sortFilter,
): Platform[] {
  let list: Platform[] = integrations.sort(sortByLabel);

  if (sortField === sortFilter.connectionType) {
    const twoWayIntegrations = integrations
      .filter((item) => item.type === 'two_way')
      .sort(sortByLabel);
    const oneWayIntegrations = integrations
      .filter((item) => item.type === 'one_way')
      .sort(sortByLabel);
    const virtualIntegrations = integrations
      .filter((item) => item.type === 'virtual')
      .sort(sortByLabel);

    list = [
      ...twoWayIntegrations,
      ...oneWayIntegrations,
      ...virtualIntegrations,
    ];
  }

  return list;
}

export function filterByOmniConnectorFlag(
  platform: Platform,
  flags: Record<string, boolean>,
): boolean {
  const omniConnectorFlagValue = flags[platform.platform];
  return omniConnectorFlagValue !== undefined
    ? omniConnectorFlagValue === platform.isOmni
    : true;
}

export const getFilteredIntegrations = (
  filters: FilterType,
  integrations: Platform[],
  omniConnectorFlags: Record<string, boolean>,
): Platform[] => {
  const { category, channel, statusFilter, sortField, searchValue } = filters;

  const newIntegrations = integrations.filter(
    (item) =>
      filterByStatus(item, statusFilter) &&
      filterBySearchValue(item, searchValue) &&
      filterByActiveFilters(item, category, channel) &&
      filterByOmniConnectorFlag(item, omniConnectorFlags),
  );

  return sortIntegrations(newIntegrations, sortField);
};
